import { reviewedFaq } from "@/lib/reviewed-faq";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2 } from "lucide-react";
import { getSeoPage } from "@/lib/seo.functions";
import { SeoCityPage } from "@/components/site/SeoCityPage";
import { faqJsonLd, breadcrumbJsonLd, serviceJsonLd } from "@/lib/seo.schemas";

const SEGMENT = "hogskola";
const PILLAR_LABEL = "Universitet & högskola";

export const Route = createFileRoute("/hogskola/$stad")({
  loader: async ({ params }) => {
    const page = await getSeoPage({ data: { segment: SEGMENT, city: params.stad } });
    if (!page) throw notFound();
    return { page };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: PILLAR_LABEL }] };
    const { page } = loaderData;
    return {
      meta: [
        { title: page.meta_title },
        { name: "description", content: page.meta_description },
        { property: "og:title", content: page.meta_title },
        { property: "og:description", content: page.meta_description },
        { property: "og:url", content: `/${SEGMENT}/${params.stad}` },
      ],
      links: [{ rel: "canonical", href: `https://eventsverige.se/${SEGMENT}/${params.stad}` }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(faqJsonLd(reviewedFaq(page.faq))) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            serviceJsonLd(page.h1, page.intro, `/${SEGMENT}/${params.stad}`, page.city_name),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Hem", url: "/" },
              { name: PILLAR_LABEL, url: `/${SEGMENT}` },
              { name: page.city_name, url: `/${SEGMENT}/${params.stad}` },
            ]),
          ),
        },
      ],
    };
  },
  component: StadPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold">Vi har inte byggt den här stadssidan än</h1>
      <p className="mt-4 text-muted-foreground">
        Men vi levererar troligen där ändå — skicka en förfrågan så återkommer vi.
      </p>
    </div>
  ),
});

function StadPage() {
  const { stad } = Route.useParams();
  const fn = useServerFn(getSeoPage);
  const { data, isLoading } = useQuery({
    queryKey: ["seo", SEGMENT, stad],
    queryFn: () => fn({ data: { segment: SEGMENT, city: stad } }),
    initialData: Route.useLoaderData().page,
  });
  if (isLoading || !data)
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  return <SeoCityPage page={data} />;
}
